import React, { useState } from 'react';
import { message, Select } from 'antd';
import { useMutation } from '@apollo/client';
import HelpArticleButton from 'utils/HelpArticleButton';
import { UPDATE_PROJECT } from '../project_settings/queries';
import {
  AcceptingFeedback,
  getIsAcceptingFeedback
} from '../project_settings/forms/sidebar/AcceptingFeedback';
import * as sidebarStrings from '../project_settings/forms/sidebar/strings';
import * as translations from './strings';
import { getLangKey } from 'appJS/models/Language';
import styles from './index.module.css';
import { SettingRow, SettingSection } from './SettingLayout';

const sidebarCopy = sidebarStrings[getLangKey()];
const strings = translations[getLangKey()];

const displayColumnName = (name: string) =>
  name ? name.charAt(0).toUpperCase() + name.slice(1) : name;

type Project = {
  id: string;
  acceptingFeedback: boolean;
  acceptingFeedbackEndDate: string;
  changeGuestDefaultColumn: boolean;
  guestDefaultColumnId: string | null;
  columns: {
    id: string;
    name: string;
    firstColumn: boolean;
  }[];
  organization: {
    acceptingFeedbackAllowed: boolean;
    feedbackDeadlineAllowed: boolean;
    id: string;
  };
  owner: {
    email: string;
  } | null;
};

type Props = {
  project: Project;
  billing: boolean;
  container?: HTMLDivElement;
};

enum LoadingKey {
  AcceptingFeedback = 'acceptingFeedback',
  ChangeGuestDefaultColumn = 'changeGuestDefaultColumn',
  GuestDefaultColumn = 'guestDefaultColumn',
  AcceptingFeedbackEndDate = 'acceptingFeedbackEndDate'
}

type Loading = {
  [key in LoadingKey]: boolean;
};

export const ClientFeedbackSettings: React.FC<Props> = ({
  project,
  billing,
  container
}) => {
  const {
    acceptingFeedback: initialAcceptingFeedback,
    acceptingFeedbackEndDate: initialAcceptingFeedbackEndDate,
    changeGuestDefaultColumn: initialChangeGuestDefaultColumn,
    guestDefaultColumnId: initialGuestDefaultColumnId,
    columns,
    organization: { acceptingFeedbackAllowed, feedbackDeadlineAllowed }
  } = project;

  const [acceptingFeedback, setAcceptingFeedback] = useState(
    initialAcceptingFeedback
  );
  const [acceptingFeedbackEndDate, setAcceptingFeedbackEndDate] = useState<
    string | null
  >(initialAcceptingFeedbackEndDate || null);
  const [changeGuestDefaultColumn, setChangeGuestDefaultColumn] = useState(
    initialChangeGuestDefaultColumn
  );
  const [guestDefaultColumnId, setGuestDefaultColumnId] = useState(
    initialGuestDefaultColumnId
  );
  const [showFeedbackDateModal, setShowFeedbackDateModal] = useState(false);
  const [loading, setLoading] = useState<Loading>({
    [LoadingKey.AcceptingFeedback]: false,
    [LoadingKey.ChangeGuestDefaultColumn]: false,
    [LoadingKey.GuestDefaultColumn]: false,
    [LoadingKey.AcceptingFeedbackEndDate]: false
  });

  const [updateProject] = useMutation(UPDATE_PROJECT, {
    onError: () => {
      message.error(
        'Something went wrong while updating the project, please try again.'
      );
    }
  });

  const syncAcceptingFeedbackState = (
    nextAcceptingFeedback = acceptingFeedback,
    nextAcceptingFeedbackEndDate = acceptingFeedbackEndDate
  ) => {
    window.project_board_props.setAcceptingFeedback?.(
      getIsAcceptingFeedback({
        acceptingFeedback: nextAcceptingFeedback,
        feedbackDeadlineAllowed,
        acceptingFeedbackEndDate: nextAcceptingFeedbackEndDate
      })
    );
  };

  const saveSettings = async (
    body: {
      acceptingFeedback?: boolean;
      acceptingFeedbackEndDate?: string | null;
      changeGuestDefaultColumn?: boolean;
      guestDefaultColumnId?: string | null;
    },
    loadingKey: LoadingKey,
    onError?: () => void
  ) => {
    setLoading(previousValue => ({ ...previousValue, [loadingKey]: true }));

    const nextAcceptingFeedback = body.acceptingFeedback ?? acceptingFeedback;
    const nextAcceptingFeedbackEndDate =
      body.acceptingFeedbackEndDate !== undefined
        ? body.acceptingFeedbackEndDate
        : acceptingFeedbackEndDate;

    try {
      await updateProject({
        variables: {
          projectId: project.id,
          ...body
        }
      });
      message.success(sidebarCopy.updateSuccess);

      if (
        body.acceptingFeedback !== undefined ||
        body.acceptingFeedbackEndDate !== undefined
      ) {
        syncAcceptingFeedbackState(
          nextAcceptingFeedback,
          nextAcceptingFeedbackEndDate
        );
      }
    } catch {
      onError?.();
    } finally {
      setLoading(previousValue => ({ ...previousValue, [loadingKey]: false }));
    }
  };

  const isAcceptingFeedback = getIsAcceptingFeedback({
    acceptingFeedback,
    feedbackDeadlineAllowed,
    acceptingFeedbackEndDate
  });

  const selectedColumn =
    columns.find(column => column.id === guestDefaultColumnId) ??
    columns.find(column => column.firstColumn) ??
    columns[0];
  const columnName = selectedColumn
    ? displayColumnName(selectedColumn.name)
    : 'the first column';
  const isLastColumn =
    !!selectedColumn &&
    columns.length > 1 &&
    selectedColumn.id === columns[columns.length - 1].id;
  const destinationDescription = !changeGuestDefaultColumn
    ? strings.staysInFeedback
    : isLastColumn
      ? strings.skipsToLastColumn(columnName)
      : strings.skipsFeedbackTab(columnName);
  const modalContainer = container || document.body;
  const destinationLoading =
    loading[LoadingKey.ChangeGuestDefaultColumn] ||
    loading[LoadingKey.GuestDefaultColumn];

  return (
    <SettingSection title={strings.feedbackCollection}>
      {acceptingFeedbackAllowed && (
        <div className={styles.acceptingFeedbackBlock}>
          <AcceptingFeedback
            compact
            feedbackDeadlineAllowed={feedbackDeadlineAllowed}
            acceptingFeedbackAllowed={acceptingFeedbackAllowed}
            isAcceptingFeedback={isAcceptingFeedback}
            acceptingFeedback={acceptingFeedback}
            acceptingFeedbackLoading={loading[LoadingKey.AcceptingFeedback]}
            onAcceptingFeedbackChange={checked => {
              setAcceptingFeedback(checked);
              saveSettings(
                { acceptingFeedback: checked },
                LoadingKey.AcceptingFeedback,
                () => setAcceptingFeedback(!checked)
              );
            }}
            acceptingFeedbackEndDate={acceptingFeedbackEndDate}
            setAcceptingFeedbackEndDate={setAcceptingFeedbackEndDate}
            onScheduleEndDate={date => {
              saveSettings(
                { acceptingFeedbackEndDate: date },
                LoadingKey.AcceptingFeedbackEndDate
              );
            }}
            onRemoveSchedule={() => {
              setAcceptingFeedbackEndDate(null);
              saveSettings(
                { acceptingFeedbackEndDate: null },
                LoadingKey.AcceptingFeedbackEndDate,
                () =>
                  setAcceptingFeedbackEndDate(
                    initialAcceptingFeedbackEndDate || null
                  )
              );
            }}
            onPauseFeedbackCollection={() => {
              setAcceptingFeedbackEndDate(null);
              setAcceptingFeedback(false);
              saveSettings(
                {
                  acceptingFeedbackEndDate: null,
                  acceptingFeedback: false
                },
                LoadingKey.AcceptingFeedbackEndDate,
                () => {
                  setAcceptingFeedbackEndDate(
                    initialAcceptingFeedbackEndDate || null
                  );
                  setAcceptingFeedback(initialAcceptingFeedback);
                }
              );
            }}
            setShowFeedbackDateModal={setShowFeedbackDateModal}
            showFeedbackDateModal={showFeedbackDateModal}
            container={modalContainer}
            billing={billing}
            organizationId={Number(project?.organization?.id || 0)}
            owner={project?.owner}
            scheduleLoading={loading[LoadingKey.AcceptingFeedbackEndDate]}
          />
        </div>
      )}
      <SettingRow
        checked={changeGuestDefaultColumn}
        loading={loading[LoadingKey.ChangeGuestDefaultColumn]}
        label={
          <span>
            {strings.sendFeedbackTo}{' '}
            {selectedColumn && (
              <Select
                style={{ width: 160 }}
                disabled={!changeGuestDefaultColumn || destinationLoading}
                loading={loading[LoadingKey.GuestDefaultColumn]}
                value={selectedColumn.id}
                options={columns.map(column => ({
                  value: column.id,
                  label: displayColumnName(column.name)
                }))}
                onChange={columnId => {
                  const previousColumnId = guestDefaultColumnId;
                  setGuestDefaultColumnId(columnId);
                  saveSettings(
                    { guestDefaultColumnId: columnId },
                    LoadingKey.GuestDefaultColumn,
                    () => setGuestDefaultColumnId(previousColumnId)
                  );
                }}
                getPopupContainer={() => modalContainer}
                popupMatchSelectWidth={false}
              />
            )}
          </span>
        }
        description={
          <>
            {destinationDescription}{' '}
            <HelpArticleButton articleId="36352" style={{ color: '#3f71e0' }}>
              {sidebarCopy.learnMore}
            </HelpArticleButton>
          </>
        }
        onChange={checked => {
          const previousColumnId = guestDefaultColumnId;
          const nextColumnId = selectedColumn?.id ?? null;
          setChangeGuestDefaultColumn(checked);
          if (nextColumnId) setGuestDefaultColumnId(nextColumnId);
          saveSettings(
            {
              changeGuestDefaultColumn: checked,
              guestDefaultColumnId: nextColumnId
            },
            LoadingKey.ChangeGuestDefaultColumn,
            () => {
              setChangeGuestDefaultColumn(!checked);
              setGuestDefaultColumnId(previousColumnId);
            }
          );
        }}
      />
    </SettingSection>
  );
};
